import { Array, Effect, pipe } from "effect";
import { CmcToken, CmcTokenMeta } from "../cmc-client/types";
import { KvClientError, KvService } from "./types";
import { getTokenCmcMetaKey } from "./keys";
import { Schema } from "@effect/schema";
import { TokenUid } from "@silo/types";

export type GetCmcTokenArray = ReturnType<typeof getCmcTokenArray>;
export type GetCmcTokenArraySuccess = Effect.Effect.Success<GetCmcTokenArray>;
export type GetCmcTokenArrayError = Effect.Effect.Error<GetCmcTokenArray>;
export type GetCmcTokenArrayContext = Effect.Effect.Context<GetCmcTokenArray>;

export function getCmcTokenArray(tokens: TokenUid[]) {
  return Effect.gen(function* () {
    if (tokens.length === 0) {
      return { satisfying: [], excluded: [] };
    }

    const kv = yield* KvService;

    const results = yield* Effect.tryPromise({
      try: async () => {
        const pipeline = kv.pipeline();

        for (const { chainKey, tokenAddress } of tokens) {
          const key = getTokenCmcMetaKey(chainKey, tokenAddress);

          pipeline.json.get(key);
        }

        return pipeline.exec();
      },
      catch: (cause) => new KvClientError({ cause }),
    });

    const [excluded, satisfying] = yield* Effect.partition(
      tokens,
      (token, i) => {
        const innerEffect = pipe(
          results,
          Array.get(i),
          Effect.andThen(Schema.decodeUnknown(Schema.NullOr(CmcTokenMeta))),
          Effect.andThen(Effect.fromNullable),
          Effect.map((meta): CmcToken => ({ ...token, meta })),
          Effect.mapError((error) => ({ token, error }))
        );

        return innerEffect;
      }
    );

    return { satisfying, excluded };
  });
}
